import { useEffect, useRef, useState } from 'react';

import { useAuth0TokenContext } from 'contexts/Auth0TokenContext';
import { EXCEL_ADDIN_AUTH_PARAM, OFFICE_JS_URL } from './constants';
import {
    clearDialogFlags, hasLogoutStarted, markLogoutStarted, sendResultToTaskPane,
} from './helpers';
import { ExcelAddinDialogMode } from './types';
import './ExcelAddinAuthBridge.scss';

export interface ExcelAddinAuthBridgeProps {
    mode: ExcelAddinDialogMode;
}

const STATUS_BY_MODE: Record<ExcelAddinDialogMode, string> = {
    signIn: 'Signing you in to Northspyre…',
    signOut: 'Signing you out…',
};

/**
 * Brokers Auth0 sign-in and sign-out for the Excel add-in task pane.
 *
 * The pane cannot run these flows itself: Auth0 matches callback and logout URLs exactly, and the only
 * URL registered for this app is the app's own origin. Whoever starts a login must also finish it,
 * because auth0-spa-js holds the PKCE transaction. So the pane opens this page in an Office dialog, the
 * app performs the flow as it normally would, and this component reports the result back to the pane
 * with `messageParent`. No Auth0 tenant change is needed, in any environment.
 *
 * This renders instead of the app router while the dialog is open, so the dialog stays light - no
 * navbar, no LaunchDarkly, no analytics session for what is really an auth window.
 *
 * Full flow, constraints, and known traps: excel-addin/docs/authentication.md
 */
export const ExcelAddinAuthBridge = ({ mode }: ExcelAddinAuthBridgeProps) => {
    const {
        auth0Loading, isLoading, isAuthenticated, accessToken, login, logout,
    } = useAuth0TokenContext();
    const [error, setError] = useState<string | null>(null);
    const hasReported = useRef(false);

    useEffect(() => {
        if (auth0Loading || isLoading) {
            return;
        }

        const reportToPane = (result: object) => {
            if (hasReported.current) {
                return;
            }
            hasReported.current = true;
            sendResultToTaskPane(result, OFFICE_JS_URL)
                .then(clearDialogFlags)
                .catch((handoffError: unknown) => setError(String(handoffError)));
        };

        if (mode === 'signOut') {
            // Deliberately not gated on isAuthenticated. Silent auth cannot reach its third-party
            // cookies inside an Office dialog, so the SPA reports "not authenticated" even while a
            // live Auth0 session cookie exists - and skipping the logout on that basis left the
            // session intact, so the next sign-in completed silently.
            if (!hasLogoutStarted()) {
                markLogoutStarted();
                // returnTo must match an Allowed Logout URL in Auth0 exactly. The app's user menu
                // logs out to the bare origin, so reusing that needs no tenant change - and the mode
                // survives the round trip in session storage, not in the URL.
                logout({ logoutParams: { returnTo: window.location.origin } });
                return;
            }
            reportToPane({ signedOut: true });
            return;
        }

        if (!isAuthenticated) {
            // Keep the marker in returnTo as well as session storage, so the flow survives either
            // being restored.
            login({ appState: { returnTo: `/?${EXCEL_ADDIN_AUTH_PARAM}=1` } });
            return;
        }

        if (!accessToken) {
            return;
        }

        reportToPane({ accessToken });
    }, [auth0Loading, isLoading, isAuthenticated, accessToken, login, logout, mode]);

    return (
        <div className="ExcelAddinAuthBridge">
            <p>{error ?? STATUS_BY_MODE[mode]}</p>
        </div>
    );
};

export default ExcelAddinAuthBridge;
